import jsPDF from 'jspdf';
import type { SignerRelationship } from '../types/family';

interface HipaaPdfOptions {
  consentText: string;
  signatureDataUrl: string;
  fullName: string;
  dateOfBirth: string;
  dateSigned: string;
  consentVersion: string;
  /** Optional: relationship of the signer to the patient */
  signerRelationship?: SignerRelationship;
  /** Optional: patient name (when parent is signing for child) */
  patientName?: string;
  /** Optional: parent name (when parent is signing for child) */
  parentName?: string;
  /** Whether the attestation checkbox was checked */
  attestationChecked?: boolean;
}

/**
 * Generate a signed HIPAA consent PDF with the drawn signature embedded.
 * Returns a Blob ready for upload.
 */
export function generateHipaaPdf(opts: HipaaPdfOptions): Blob {
  const {
    consentText,
    signatureDataUrl,
    fullName,
    dateSigned,
    consentVersion,
    signerRelationship = 'self',
    patientName,
    parentName,
    attestationChecked = false,
  } = opts;

  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 20;
  const contentWidth = pageWidth - marginX * 2;
  let y = 20;

  /* ---------------------------
     HEADER
  ---------------------------- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 120);
  doc.text('mediloop inc.', marginX, y);
  doc.setFontSize(8);
  doc.text(`Consent Version: ${consentVersion}`, pageWidth - marginX, y, {
    align: 'right',
  });

  y += 4;
  doc.setDrawColor(200, 200, 220);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 10;

  /* ---------------------------
     TITLE
  ---------------------------- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 50);
  doc.text('HIPAA Release Authorization', pageWidth / 2, y, {
    align: 'center',
  });
  y += 6;

  // Subtitle for family member contexts
  if (signerRelationship === 'parent' && patientName) {
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 100);
    doc.text(
      `Minor Patient — Signed by Parent/Legal Guardian`,
      pageWidth / 2,
      y,
      { align: 'center' }
    );
    y += 4;
  } else if (signerRelationship === 'spouse') {
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 100);
    doc.text('Spouse Authorization', pageWidth / 2, y, { align: 'center' });
    y += 4;
  }

  y += 4;

  /* ---------------------------
     CONSENT BODY
  ---------------------------- */
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 60);

  const lines = doc.splitTextToSize(consentText, contentWidth);
  const lineHeight = 4;

  for (const line of lines) {
    if (y + lineHeight > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }

    if (/^\d+\.\s/.test(line.trim())) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    doc.text(line, marginX, y);
    y += lineHeight;
  }

  /* ---------------------------
     SIGNATURE BLOCK
  ---------------------------- */
  if (y + 80 > pageHeight - 20) {
    doc.addPage();
    y = 20;
  }

  y += 8;
  doc.setDrawColor(200, 200, 220);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 50);

  if (signerRelationship === 'parent') {
    doc.text('Signature of Parent/Legal Guardian', marginX, y);
  } else {
    doc.text('Signature', marginX, y);
  }
  y += 8;

  const sigWidth = 70;
  const sigHeight = 25;
  doc.addImage(signatureDataUrl, 'PNG', marginX, y, sigWidth, sigHeight);

  y += sigHeight + 2;
  doc.setDrawColor(160, 160, 180);
  doc.setLineWidth(0.4);
  doc.line(marginX, y, marginX + sigWidth, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 80);
  doc.text(`Printed Name: ${fullName}`, marginX, y);
  y += 5;

  if (signerRelationship === 'parent' && parentName) {
    doc.text(`Parent/Guardian: ${parentName}`, marginX, y);
    y += 5;
    if (patientName) {
      doc.text(`Patient (Minor): ${patientName}`, marginX, y);
      y += 5;
    }
  }

  doc.text(`Date Signed: ${dateSigned}`, marginX, y);
  y += 5;
  doc.text('Signature Method: Drawn (electronic)', marginX, y);
  y += 5;

  if (attestationChecked) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0, 100, 0);
    doc.text(`Legal Attestation: Confirmed on ${dateSigned}`, marginX, y);
    y += 5;
  }

  /* ---------------------------
     FOOTER
  ---------------------------- */
  const footerY = pageHeight - 10;
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 170);
  doc.text(
    'This document was electronically signed via Mediloop. ' +
      'The signature above is a legally binding electronic signature.',
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );

  return doc.output('blob');
}
